import React, { useCallback, useState } from 'react';
import { Database } from 'lucide-react';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch from './toolkit/useToolkitBatch';
import useOnPrem, { rowsOf } from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Internal Notes — the desktop's OHIP › Internal Notes.
//
// v5 profile notes, internal reservation comments and internal block notes onto their Opera Cloud records:
//   1. Read — from the property's OPERA v5 through the project's SyncConnect (toolkit onprem.js notes-*), or for
//      profiles from a PR_NOTES XML report (files.js profile-notes). One row per note; grouped here per record.
//   2. Post — one record per call (toolkit notes.js): the Cloud record is found by its v5 key (profile by NAME_ID,
//      reservation by v5 confirmation number, block by code), and only notes whose text it does not already carry are
//      added — so running again never duplicates. A record not in Cloud yet is "Not in Cloud" and can be run later.
//
// NOT HERE: the desktop's "Dedupe Reservation Notes" tab, which deletes Cloud comments — a separate, Cloud-only tool.

const today = () => new Date().toISOString().slice(0, 10);

// One entry per tab: the catalogue query, its options, and how a note row is grouped and posted.
const PROFILE = {
    group: (r) => r.name_id,
    record: (id, rows) => ({ NAME_ID: id, notes: rows.map((r) => ({ type: r.note_type, text: r.note_text, internal: r.internal_yn === 'Y', title: r.note_title || '' })) }),
    post: (row) => ['post-profile-notes', { nameId: row.NAME_ID, notes: row.notes }],
    idLabel: 'v5 Name ID', idKey: 'NAME_ID',
};
const TABS = [
    { id: 'profiles-with-res', label: 'Profiles with reservations', query: 'notes-profiles-with-res', cutoff: true, includeAll: true, xml: true, ...PROFILE,
      text: 'Profile notes of profiles with a reservation departing on or after the cutoff.' },
    { id: 'profiles', label: 'All profiles', query: 'notes-profiles', includeAll: true, xml: true, ...PROFILE,
      text: 'Notes of every profile at the property, with or without a reservation.' },
    { id: 'reservations', label: 'Reservation comments', query: 'notes-reservations', cutoff: true,
      group: (r) => r.confirmation_no,
      record: (id, rows) => ({ CONFIRMATION_NO: id, notes: rows.map((r) => ({ type: r.comment_type, text: r.comment_text, internal: r.internal_yn === 'Y' })) }),
      post: (row) => ['post-reservation-comments', { confirmationNo: row.CONFIRMATION_NO, comments: row.notes }],
      idLabel: 'v5 Confirmation', idKey: 'CONFIRMATION_NO',
      text: 'Internal comments on reservations departing on or after the cutoff.' },
    { id: 'blocks', label: 'Block notes', query: 'notes-blocks',
      group: (r) => r.allotment_code,
      record: (id, rows) => ({ BLOCK_CODE: id, notes: rows.map((r) => ({ type: r.note_type, text: r.note_text })) }),
      post: (row) => ['post-block-notes', { blockCode: row.BLOCK_CODE, notes: row.notes }],
      idLabel: 'Block code', idKey: 'BLOCK_CODE',
      text: 'Internal notes on blocks ending on or after the business date.' },
];

// Note rows (lower-case, from either source) → one batch row per record, with a count and the note types for the grid.
const groupRows = (tab, rows) => {
    const by = new Map();
    for (const r of rows) {
        const id = tab.group(r);
        if (id == null || id === '') continue;
        if (!by.has(String(id))) by.set(String(id), []);
        by.get(String(id)).push(r);
    }
    return [...by.entries()].map(([id, list]) => {
        const rec = tab.record(id, list);
        return { ...rec, noteCount: rec.notes.length, types: [...new Set(rec.notes.map((n) => n.type))].join(', ') };
    });
};

function ToolkitInternalNotesPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const { ask } = useOnPrem(ctx);
    const [tabId, setTabId] = useState(TABS[0].id);
    const tab = TABS.find((t) => t.id === tabId);
    const [cutoff, setCutoff] = useState(today());
    const [includeAll, setIncludeAll] = useState(false);
    const [read, setRead] = useState({ state: STEP.IDLE, message: '' });

    const request = useCallback((row) => { const [op, args] = tab.post(row); return { area: 'notes', op, args }; }, [tab]);
    const batch = useToolkitBatch({ request, ctx,
        server: { screen: `internal-notes-${tab.id}`, title: `Internal Notes — ${tab.label}`, keyOf: (row) => row[tab.idKey] } });
    const { load } = batch;

    const switchTab = (id) => {
        if (batch.running || id === tabId) return;
        setTabId(id);
        setRead({ state: STEP.IDLE, message: '' });
        load([]);
    };

    const loaded = (rows, from) => {
        const records = groupRows(tab, rows);
        load(records);
        return `${rows.length} note(s) on ${records.length} record(s) ${from}.`;
    };

    const readDatabase = async () => {
        setRead({ state: STEP.BUSY, message: 'Asking SyncConnect for the notes…' });
        try {
            const params = { ...(tab.cutoff ? { cutoffDate: cutoff } : {}), ...(tab.includeAll ? { includeAll: includeAll ? 'Y' : 'N' } : {}) };
            const d = await ask(tab.query, params);
            if (d.status !== 'RETURNED') { setRead({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}) — try again shortly.` }); return; }
            const msg = loaded(rowsOf(d.result), 'from Opera v5');
            setRead({ state: d.result_truncated === 'Y' ? STEP.ERROR : STEP.DONE,
                message: d.result_truncated === 'Y' ? `${msg} The answer was cut short — not every note is here; narrow the cutoff and run again.` : msg });
        } catch (e) {
            setRead({ state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    const columns = [
        { key: tab.idKey, label: tab.idLabel },
        { key: 'noteCount', label: 'Notes' },
        { key: 'types', label: 'Types' },
        { key: 'posted', label: 'Posted' },
        { key: 'alreadyThere', label: 'Already in Cloud' },
        { key: 'targetId', label: 'Cloud id' },
    ];

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Internal Notes"
                subtitle="Copy v5 profile notes, reservation comments and block notes onto their Opera Cloud records — never twice." />

            <div className="tkb-tabs">
                {TABS.map((t) => (
                    <button key={t.id} type="button" className={`tkb-tab${t.id === tabId ? ' tkb-tab-active' : ''}`}
                        onClick={() => switchTab(t.id)} disabled={batch.running}>{t.label}</button>
                ))}
            </div>
            <p className="tkb-hint">{tab.text}</p>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Read from Opera v5 {stepIcon(read.state)}</div>
                <div className="tkb-row">
                    {tab.cutoff && <label><span>Departing on or after</span><input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} /></label>}
                    {tab.includeAll && (
                        <label className="tkb-check">
                            <input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} />
                            <span>Include non-internal notes</span>
                        </label>
                    )}
                    <Button variant="secondary" onClick={readDatabase} disabled={!ready || read.state === STEP.BUSY || batch.running || (tab.cutoff && !cutoff)}>
                        <Database className="icon" /> Read through SyncConnect
                    </Button>
                </div>
                {read.message && <p className={`tkb-msg${read.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{read.message}</p>}
            </div>

            {tab.xml && (
                <ToolkitFileStep number={2} title="…or a PR_NOTES report" label="Choose PR_NOTES XML" accept=".xml"
                    kind="profile-notes" parseOptions={{ includeAll }} disabled={!ready || batch.running}
                    onParsed={(rows, { file }) => setRead({ state: STEP.DONE, message: loaded(rows, `from ${file.name}`) })} />
            )}

            <ToolkitBatchTable batch={batch} columns={columns} searchKeys={[tab.idKey, 'types']} exportName={`Internal_Notes_${tab.id}`}
                canRun={ready} onClear={() => load([])} />
        </div>
    );
}

export default ToolkitInternalNotesPage;
